import { useCallback, useEffect, useRef, useState } from "react";
import type { CatalogResponse } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type CatalogState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: CatalogResponse };

export interface UseCatalogResult {
  state: CatalogState;
  refetch: () => void;
}

// Shape copied from useLabResults.ts -- fetch on mount plus on-demand
// refetch, no poll, no react-query.
export function useCatalog(): UseCatalogResult {
  const api = useApi();
  const [state, setState] = useState<CatalogState>({ status: "loading" });
  const cancelledRef = useRef(false);

  const fetchCatalog = useCallback(() => {
    api.commerce
      .getCatalog()
      .then((data) => {
        if (!cancelledRef.current) {
          setState({ status: "success", data });
        }
      })
      .catch((err: unknown) => {
        if (!cancelledRef.current) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load catalog" });
        }
      });
  }, [api]);

  useEffect(() => {
    cancelledRef.current = false;
    fetchCatalog();
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchCatalog]);

  return { state, refetch: fetchCatalog };
}
